import React, { useEffect, useRef, useState } from 'react'

import { useFreeBuyBonusStore, useConfigurationStore, useBuyBonusStore } from '@stores'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { useFormatMoney, useLoadAtlasesWhenGameSettingsReady, useScaleToFit } from '@hooks'
import { useTranslation } from '@amusnet/translations/react'
import { getBuyBonusAtlasPaths } from '@utils/gameSettings'
import { resolveFreeBuyBonusImage } from '@utils/bonus'
import { FreeBuyBonusBonus } from '@amusnet/mgs-ws-client'

interface FBBSingleBonusProps {
    updateSelectedBonus: (bonus: FreeBuyBonusBonus) => void
}

const FBBSingleBonus: React.FC<FBBSingleBonusProps> = ({ updateSelectedBonus }: FBBSingleBonusProps) => {
    const bonuses = useFreeBuyBonusStore((state) => state.bonuses)
    const styles = useFreeBuyBonusStore((state) => state.styles)

    const findElementByImageName = useBuyBonusStore((state) => state.findElementByImageName)
    const atlasMap = useBuyBonusStore((state) => state.atlasMap)
    const cachedImages = useBuyBonusStore((state) => state.cachedImages)
    const loadAtlasesFromPath = useBuyBonusStore((state) => state.loadAtlasesFromPath)

    const formatMoney = useFormatMoney()
    const { t } = useTranslation()
    const currency = useConfigurationStore((state) => state.currency)

    const bonusContainerRef = useRef<HTMLDivElement | null>(null)
    const bonusContentRef = useRef<HTMLDivElement | null>(null)

    const [betValue, setBetValue] = useState('')

    useLoadAtlasesWhenGameSettingsReady(getBuyBonusAtlasPaths, loadAtlasesFromPath)

    const image =
        atlasMap && bonuses[0]
            ? resolveFreeBuyBonusImage(
                  findElementByImageName,
                  bonuses[0].typeConfiguration?.freeBuyBonusConfiguration?.imageName
              )
            : undefined

    const imageStyles = {
        ...(image ? buildAtlasImageStyles(image.atlasName, image.value, cachedImages[image.atlasName]) : {}),
        height: image ? `${image.value.height + 38}px` : undefined, // TODO: check with the designers why the height of in the resource is never okay and needs this correction
    }

    useEffect(() => {
        if (bonuses.length === 1) {
            updateSelectedBonus(bonuses[0])
            setBetValue(formatMoney(Number(bonuses[0].betValue)))
        }
    }, [bonuses, formatMoney])

    const freeBonusNameText = t('gameui.bonusSystem.freeBonusName', {
        bonusName: bonuses[0].typeConfiguration?.freeBuyBonusConfiguration?.label ?? '',
    })

    const scale = useScaleToFit(bonusContainerRef, bonusContentRef, [
        t('gameui.bonusSystem.youAreAwarded'),
        freeBonusNameText,
    ])

    return (
        <div
            id="fbb-single-bonus"
            ref={bonusContainerRef}
            className="flex flex-col items-center justify-between w-[746px]"
        >
            <div id="fbb-single-bonus-content" className="flex flex-col w-full">
                <div id="fbb-single-bonus-image" style={imageStyles} />

                <div
                    id="free-buy-bonus-text"
                    style={{ ...styles.bodyBackground }}
                    className="flex flex-col items-center mt-[-39px] pb-4 whitespace-nowrap"
                >
                    <div
                        id="fbb-single-bonus-awarded"
                        ref={bonusContentRef}
                        className=" flex flex-col items-center"
                        style={{ zoom: scale }}
                    >
                        <p
                            id="fbb-single-bonus-awarded-label"
                            style={{ color: styles.secondaryTextColor }}
                            className="text-4xl font-bold"
                        >
                            {t('gameui.bonusSystem.youAreAwarded')}
                        </p>
                        <p
                            id="fbb-single-bonus-name"
                            style={{ color: styles.primaryTextColor }}
                            className="text-4xl font-black leading-[2.2]"
                        >
                            {freeBonusNameText}
                        </p>
                    </div>
                    <p
                        id="fbb-single-bonus-bet-amount"
                        style={{ color: styles.secondaryTextColor }}
                        className="text-4xl font-bold"
                    >
                        x {betValue} {currency}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default FBBSingleBonus
